<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['labelTabChange']);
const { t } = useI18n();

const scrollContainerRef = ref(null);
const isDragging = ref(false);
const dragStartX = ref(0);
const initialScrollLeft = ref(0);
const hasDragged = ref(false);

const hasItems = computed(() => props.items.length > 0);
const allItem = computed(() => ({
  id: 'all-labels',
  title: t('CHAT_LIST.FILTER_ALL'),
  color: 'transparent',
}));
const tabTitle = title => {
  const text = String(title || '').trim();
  if (!text) return text;
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
};

const itemsWithAll = computed(() => [
  { ...allItem.value, displayTitle: tabTitle(allItem.value.title) },
  ...props.items.map(item => ({ ...item, displayTitle: tabTitle(item.title) })),
]);
const isAllTabActive = computed(() => !props.activeTab);

const isTabActive = item =>
  (item.id === 'all-labels' && isAllTabActive.value) ||
  props.activeTab === item.title;

const tabColor = item =>
  item.id === 'all-labels' ? '#1f93ff' : item.color || '#1f93ff';

const tabStyle = item => {
  if (!isTabActive(item)) return {};

  return {
    color: tabColor(item),
  };
};

const onMouseDown = event => {
  if (!scrollContainerRef.value) return;

  isDragging.value = true;
  hasDragged.value = false;
  dragStartX.value = event.clientX;
  initialScrollLeft.value = scrollContainerRef.value.scrollLeft;
};

const onMouseMove = event => {
  if (!isDragging.value || !scrollContainerRef.value) return;

  const delta = event.clientX - dragStartX.value;
  if (Math.abs(delta) > 4) {
    hasDragged.value = true;
  }
  scrollContainerRef.value.scrollLeft = initialScrollLeft.value - delta;
};

const onMouseUp = () => {
  isDragging.value = false;
};

const onTabClick = label => {
  if (hasDragged.value) {
    return;
  }
  emit('labelTabChange', label);
};
</script>

<template>
  <div
    v-if="hasItems"
    ref="scrollContainerRef"
    class="flex items-center gap-7 overflow-x-auto overflow-y-hidden border-b border-n-slate-3 bg-white px-7 py-3 text-sm cursor-grab active:cursor-grabbing select-none dark:border-n-weak dark:bg-n-solid-2"
    :class="{ 'cursor-grabbing': isDragging }"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
  >
    <button
      v-for="item in itemsWithAll"
      :key="item.id"
      type="button"
      class="relative shrink-0 pb-2 -mb-3 transition-colors capitalize"
      :class="
        isTabActive(item)
          ? 'font-semibold'
          : 'font-normal text-n-slate-10 hover:text-n-slate-12'
      "
      :style="tabStyle(item)"
      @click="onTabClick(item)"
    >
      <span>{{ item.displayTitle }}</span>
      <span
        v-if="isTabActive(item)"
        class="absolute inset-x-0 bottom-0 h-0.5 rounded-full"
        :style="{ backgroundColor: tabColor(item) }"
      />
    </button>
  </div>
</template>
